import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { selectPageTitle, selectFaviconFileUrl, selectHeaderColor } from '../../common/session/session.selectors';
import { useNotifier } from '../../lib/use-notifier';
import UserSession from '../../common/user-session';
import ThemeContext from '../../providers/contexts/ThemeContext';
import { lightenColor } from '../../utils/colors';

interface ThemeContextType {
  theme: string;
}

export const Html: React.FC = ({ children }) => {
  useNotifier();
  const pageTitle = useSelector(selectPageTitle) as string;
  const faviconUrl = useSelector(selectFaviconFileUrl) as string;
  const headerColor = useSelector(selectHeaderColor) as string;
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;

  // Default favicon path - using relative path instead of import
  const defaultFavicon = '/favicon.png';

  // Function to determine if a color is dark or light
  const isColorDark = (color: string) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if color is dark
    return luminance < 0.5;
  };

  // Handle impersonation
  let finalPageTitle = pageTitle;
  let finalFaviconUrl = faviconUrl;
  let finalHeaderColor = headerColor;

  if (UserSession.getParam('isImpersonating')) {
    finalPageTitle = (UserSession.getParam('pageTitle') as string) || pageTitle;
    finalFaviconUrl = (UserSession.getParam('faviconFileUrl') as string) || faviconUrl;
    finalHeaderColor = (UserSession.getParam('headerColor') as string) || headerColor;
  }

  // Make sure we have a valid favicon URL
  const validFaviconUrl = finalFaviconUrl && finalFaviconUrl !== 'false' ? finalFaviconUrl : defaultFavicon;

  return (
    <React.Fragment>
      <Helmet titleTemplate={`%s | ${finalPageTitle}`} defaultTitle={`${finalPageTitle}`}>
        <link rel="icon" type="image/png" href={validFaviconUrl} />
        {finalHeaderColor && finalHeaderColor !== 'default' && finalHeaderColor !== '#85ba3b' && theme !== 'dark' && (
          <style type='text/css'>
            {`
              :root {
                --branding-color: ${finalHeaderColor} !important;
              }
              .cr-header {
                ${isColorDark(finalHeaderColor) 
                  ? `background: ${finalHeaderColor} !important; background-image: none !important;`
                  : `background-image: linear-gradient(173deg, 
                      ${lightenColor(finalHeaderColor, 0.10)} 0%, 
                      ${finalHeaderColor} 42%, 
                      ${lightenColor(finalHeaderColor, -0.25)} 90%
                    ) !important;`
                }
              }
              .ethica-button-green, .swal2-confirm {
                background-color: var(--branding-color, ${finalHeaderColor}) !important;
                border-color: var(--branding-color, ${finalHeaderColor}) !important;
                color: ${isColorDark(finalHeaderColor) ? '#ffffff' : '#000000'} !important;
              }
              .card-body svg, .cr-search-form svg, .pf-c-tabs__item-icon svg {
                fill: var(--branding-color, ${finalHeaderColor}) !important;
              }
              .cr-header__nav-right svg, .icon-heading svg, .dashboard-card-wrapper.site-status svg {
                fill: #fff !important;
              }
              a {
                color: var(--branding-color, ${finalHeaderColor}) !important;
              }
              .header-profile .list-group-item a svg,
              .custom-popover .list-group-item a svg,
              .list-group-item a [class*="Md"] {
                color: #495057 !important;
                fill: #495057 !important;
                width: 16px !important;
                height: 16px !important;
                margin-right: 8px !important;
                display: inline-block !important;
                vertical-align: middle !important;
              }
              .header-profile .list-group-item svg,
              .custom-popover .list-group-item svg,
              .list-group-item [class*="Md"] {
                color: #495057 !important;
                fill: #495057 !important;
                width: 16px !important;
                height: 16px !important;
                margin-right: 8px !important;
                display: inline-block !important;
                vertical-align: middle !important;
              }
            `}
          </style>
        )}
      </Helmet>
      {children}
    </React.Fragment>
  );
};
